import React, { useEffect, useState, useRef } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, MessageCircle, Send, Bot, User, ShieldAlert, Sparkles,
    ChevronRight, Info, Phone, Languages, Plus, Zap, Play, RotateCcw,
    UserCheck, Clock, AlertTriangle, CheckCircle2, Ban, HeartPulse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const STATUS_META = {
    ai_handling: { label: "AI Handling", color: "border-indigo-500/30 bg-indigo-500/10 text-indigo-200", Icon: Bot },
    waiting_for_staff: { label: "Waiting for Staff", color: "border-amber-500/30 bg-amber-500/10 text-amber-200", Icon: Clock },
    human_handling: { label: "Human Handling", color: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", Icon: UserCheck },
    resolved: { label: "Resolved", color: "border-slate-500/30 bg-slate-500/10 text-slate-300", Icon: CheckCircle2 },
};

const INTENT_LABELS = {
    faq_hours: "Hours",
    faq_location: "Location",
    faq_service: "Service info",
    faq_price: "Pricing",
    appointment_request: "Appointment",
    complaint: "Complaint",
    sales_enquiry: "Sales enquiry",
    human_handover_request: "Handover request",
    medical_advice_seeking: "Medical advice",
    spam: "Spam",
    general: "General",
};

const LANG_LABELS = { en: "EN", hi: "HI", gu: "GU", other: "?" };

export default function Inbox() {
    const { currentBusiness } = useApp();
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [thread, setThread] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("inbox");
    const [statusFilter, setStatusFilter] = useState(null);
    const [staffMsg, setStaffMsg] = useState("");
    const [sending, setSending] = useState(false);
    const listPollRef = useRef(null);

    const loadThreads = async () => {
        if (!currentBusiness) return;
        try {
            const r = await api.waThreads(currentBusiness.id, statusFilter || undefined);
            setThreads(r.threads || []);
            const ids = (r.threads || []).map((t) => t.id);
            if ((!activeId || !ids.includes(activeId)) && ids.length) setActiveId(ids[0]);
        } catch (e) { /* ignore */ }
    };

    const loadThread = async (id) => {
        if (!id) return;
        try {
            const r = await api.waThread(currentBusiness.id, id);
            setThread(r.thread);
        } catch { /* ignore */ }
    };

    // Switching business must drop the previous business's open conversation.
    const bizRef = useRef(currentBusiness?.id);
    useEffect(() => {
        if (bizRef.current !== currentBusiness?.id) {
            bizRef.current = currentBusiness?.id;
            setActiveId(null);
            setThread(null);
            setStaffMsg("");
        }
    }, [currentBusiness?.id]);

    useEffect(() => {
        setLoading(true);
        (async () => {
            await loadThreads();
            setLoading(false);
        })();
        // eslint-disable-next-line
    }, [currentBusiness?.id, statusFilter]);

    useEffect(() => {
        if (activeId) loadThread(activeId);
    }, [activeId]); // eslint-disable-line

    // Poll thread + list every 6s while user is on Inbox for near-realtime feel
    useEffect(() => {
        if (listPollRef.current) clearInterval(listPollRef.current);
        listPollRef.current = setInterval(() => {
            loadThreads();
            if (activeId) loadThread(activeId);
        }, 6000);
        return () => listPollRef.current && clearInterval(listPollRef.current);
    }, [activeId, currentBusiness?.id, statusFilter]); // eslint-disable-line

    const sendStaffReply = async () => {
        if (!staffMsg.trim() || !thread) return;
        setSending(true);
        try {
            await api.waStaffReply(thread.id, staffMsg.trim());
            setStaffMsg("");
            toast.success("Staff reply sent.");
            await loadThread(thread.id);
            await loadThreads();
        } catch { toast.error("Send failed."); }
        finally { setSending(false); }
    };

    const returnToAi = async () => {
        try {
            await api.waReturnToAi(thread.id);
            toast.success("Returned to AI.");
            await loadThread(thread.id);
            await loadThreads();
        } catch { toast.error("Failed."); }
    };
    const markResolved = async () => {
        try {
            await api.waResolveThread(thread.id);
            toast.success("Thread resolved.");
            await loadThread(thread.id);
            await loadThreads();
        } catch { toast.error("Failed."); }
    };

    return (
        <div className="max-w-[1400px] mx-auto space-y-4 fade-in" data-testid="inbox-page">
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="label-eyebrow text-emerald-300">Inbox</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                        WhatsApp AI Inbox
                    </h1>
                    <p className="mt-2 text-sm text-slate-400 flex items-center gap-2">
                        <Info className="h-3.5 w-3.5 text-amber-300" />
                        <span data-testid="inbox-simulation-banner">
                            Simulation — connect WhatsApp Business API to go live.
                        </span>
                    </p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-white/[0.03] border border-white/10">
                    <TabsTrigger value="inbox" data-testid="inbox-tab-inbox">
                        <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Conversations
                    </TabsTrigger>
                    <TabsTrigger value="simulate" data-testid="inbox-tab-simulate">
                        <Play className="h-3.5 w-3.5 mr-1.5" /> Simulate incoming
                    </TabsTrigger>
                    <TabsTrigger value="rules" data-testid="inbox-tab-rules">
                        <Zap className="h-3.5 w-3.5 mr-1.5" /> Automations
                    </TabsTrigger>
                    <TabsTrigger value="autonomy" data-testid="inbox-tab-autonomy">
                        <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Response modes
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="inbox" className="mt-4">
                    <div className="grid grid-cols-12 gap-4">
                        {/* Thread list */}
                        <div className="col-span-4 xl:col-span-3">
                            <div className="surface rounded-2xl p-3 space-y-2">
                                <div className="flex items-center gap-2 px-1">
                                    <Select
                                        value={statusFilter ?? "all"}
                                        onValueChange={(v) => setStatusFilter(v === "all" ? null : v)}
                                    >
                                        <SelectTrigger data-testid="inbox-status-filter" className="h-8 text-xs bg-white/[0.03] border-white/10 text-slate-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All statuses</SelectItem>
                                            <SelectItem value="ai_handling">AI Handling</SelectItem>
                                            <SelectItem value="waiting_for_staff">Waiting for Staff</SelectItem>
                                            <SelectItem value="human_handling">Human Handling</SelectItem>
                                            <SelectItem value="resolved">Resolved</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <ul className="divide-y divide-white/5">
                                    {loading ? (
                                        <li className="py-8 text-center text-slate-500">
                                            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                                        </li>
                                    ) : threads.length === 0 ? (
                                        <li className="py-8 text-center text-slate-500 text-sm">
                                            No conversations yet.
                                        </li>
                                    ) : (
                                        threads.map((t) => {
                                            const st = STATUS_META[t.status] || STATUS_META.ai_handling;
                                            const active = t.id === activeId;
                                            return (
                                                <li key={t.id}>
                                                    <button
                                                        onClick={() => setActiveId(t.id)}
                                                        data-testid={`thread-item-${t.id}`}
                                                        className={`w-full text-left px-3 py-3 rounded-xl transition-colors ${
                                                            active ? "bg-indigo-500/10 ring-1 ring-indigo-500/30"
                                                                   : "hover:bg-white/[0.03]"
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="text-sm font-semibold text-slate-100 truncate">
                                                                {t.customer_name || t.customer_phone}
                                                            </span>
                                                            <span className="text-[10px] mono text-slate-500 shrink-0">
                                                                {LANG_LABELS[t.last_language] || "EN"}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 text-xs text-slate-400 truncate">
                                                            {t.last_preview || "—"}
                                                        </div>
                                                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                                            <Badge variant="outline" className={`text-[10px] gap-1 ${st.color}`}>
                                                                <st.Icon className="h-2.5 w-2.5" /> {st.label}
                                                            </Badge>
                                                            {t.last_intent && (
                                                                <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400">
                                                                    {INTENT_LABELS[t.last_intent] || t.last_intent}
                                                                </Badge>
                                                            )}
                                                            {t.is_simulated && (
                                                                <Badge variant="outline" className="text-[10px] border-amber-500/30 bg-amber-500/10 text-amber-200">
                                                                    Sim
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </button>
                                                </li>
                                            );
                                        })
                                    )}
                                </ul>
                            </div>
                        </div>

                        {/* Thread view */}
                        <div className="col-span-8 xl:col-span-9">
                            <div className="surface rounded-2xl p-5 min-h-[600px] flex flex-col" data-testid="thread-view">
                                {!thread ? (
                                    <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                                        Select a conversation on the left.
                                    </div>
                                ) : (
                                    <>
                                        <ThreadHeader thread={thread} onReturnToAi={returnToAi} onResolve={markResolved} />
                                        <div className="flex-1 mt-4 overflow-y-auto max-h-[500px] pr-2 space-y-3" data-testid="thread-messages">
                                            {thread.messages?.map((m) => <MessageBubble key={m.id} m={m} />)}
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-white/5">
                                            <StaffComposer
                                                thread={thread}
                                                value={staffMsg}
                                                onChange={setStaffMsg}
                                                onSend={sendStaffReply}
                                                sending={sending}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="simulate" className="mt-4">
                    <Simulate businessId={currentBusiness?.id} onDone={loadThreads} setActiveId={setActiveId} setTab={setTab} />
                </TabsContent>

                <TabsContent value="rules" className="mt-4">
                    <RulesPanel businessId={currentBusiness?.id} />
                </TabsContent>

                <TabsContent value="autonomy" className="mt-4">
                    <AutonomyPanel businessId={currentBusiness?.id} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function ThreadHeader({ thread, onReturnToAi, onResolve }) {
    const st = STATUS_META[thread.status] || STATUS_META.ai_handling;
    return (
        <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
                <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                        <MessageCircle className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div>
                        <div className="text-lg font-bold text-slate-100" data-testid="thread-title">
                            {thread.customer_name || thread.customer_phone}
                        </div>
                        <div className="text-xs text-slate-400 flex items-center gap-2">
                            <Phone className="h-3 w-3" /> {thread.customer_phone}
                            {thread.last_language && (
                                <>
                                    <span className="text-slate-600">·</span>
                                    <Languages className="h-3 w-3" />
                                    {LANG_LABELS[thread.last_language] || "EN"}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Badge variant="outline" data-testid="thread-status-pill" className={`gap-1.5 ${st.color}`}>
                    <st.Icon className="h-3 w-3" /> {st.label}
                </Badge>
                {thread.status !== "ai_handling" && thread.status !== "resolved" && (
                    <Button size="sm" variant="outline" onClick={onReturnToAi}
                        data-testid="thread-return-to-ai"
                        className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-8">
                        <RotateCcw className="h-3 w-3" /> Return to AI
                    </Button>
                )}
                {thread.status !== "resolved" && (
                    <Button size="sm" variant="outline" onClick={onResolve}
                        data-testid="thread-resolve"
                        className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 gap-1.5 h-8">
                        <CheckCircle2 className="h-3 w-3" /> Mark resolved
                    </Button>
                )}
            </div>
        </div>
    );
}

function MessageBubble({ m }) {
    const isIn = m.direction === "in";
    const isSystem = m.author === "system";
    const isAi = m.author === "ai";
    const isStaff = m.author === "staff";
    const authorLabel = isSystem ? "System" : isAi ? "AI" : isStaff ? (m.author_name || "Staff") : (m.author_name || "Customer");
    return (
        <div className={`flex ${isIn ? "justify-start" : "justify-end"}`}
             data-testid={`msg-${m.id}`}>
            <div className={`max-w-[80%] rounded-2xl p-3 ${
                isSystem ? "bg-slate-500/10 border border-slate-500/20 text-slate-300"
                : isIn ? "bg-white/[0.05] border border-white/10 text-slate-100"
                : isAi ? "bg-indigo-500/15 border border-indigo-500/30 text-slate-100"
                : "bg-emerald-500/15 border border-emerald-500/30 text-slate-100"
            }`}>
                <div className="flex items-center gap-2 mb-1 text-[10px] mono uppercase tracking-widest text-slate-400">
                    {isAi && <Bot className="h-3 w-3" />}
                    {isStaff && <UserCheck className="h-3 w-3" />}
                    {!isAi && !isStaff && !isSystem && <User className="h-3 w-3" />}
                    {authorLabel}
                    {m.intent && <span className="ml-1 text-indigo-200">· {INTENT_LABELS[m.intent] || m.intent}</span>}
                    {m.language && <span className="ml-1 text-slate-500">· {LANG_LABELS[m.language] || m.language}</span>}
                    {m.autonomy_action && <span className="ml-1 text-amber-200">· {m.autonomy_action}</span>}
                </div>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                {m.delivery_note && (
                    <div className="mt-1.5 text-[10px] text-slate-500 italic">{m.delivery_note}</div>
                )}
                {m.is_handover_request && (
                    <div className="mt-1.5 text-[10px] text-amber-200 flex items-center gap-1">
                        <ShieldAlert className="h-3 w-3" /> Handover requested
                    </div>
                )}
                {m.is_medical_advice && (
                    <div className="mt-1.5 text-[10px] text-rose-200 flex items-center gap-1">
                        <HeartPulse className="h-3 w-3" /> Medical-advice guardrail applied
                    </div>
                )}
            </div>
        </div>
    );
}

function StaffComposer({ thread, value, onChange, onSend, sending }) {
    const disabled = thread.status === "resolved";
    return (
        <div>
            <div className="text-[10px] mono uppercase tracking-widest text-slate-500 mb-1.5">
                Reply as staff · sends to customer (simulated until live)
            </div>
            <div className="flex items-start gap-2">
                <Textarea
                    data-testid="staff-reply-input"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 bg-white/[0.02] border-white/5 text-slate-100"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                    disabled={disabled}
                />
                <Button onClick={onSend} disabled={sending || disabled || !value.trim()}
                    data-testid="staff-reply-send"
                    className="bg-emerald-500 hover:bg-emerald-400 text-white gap-1.5">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                </Button>
            </div>
        </div>
    );
}

function Simulate({ businessId, onDone, setActiveId, setTab }) {
    const [phone, setPhone] = useState("+14155559999");
    const [name, setName] = useState("Test Customer");
    const [text, setText] = useState("What are your timings?");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!text.trim() || !businessId) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.waSimulateIncoming(businessId, { customer_phone: phone, customer_name: name, text });
            setResult(r);
            await onDone?.();
            if (r.thread_id) setActiveId(r.thread_id);
            toast.success(`AI pipeline: ${r.action || "processed"}`);
        } catch { toast.error("Simulation failed."); }
        finally { setBusy(false); }
    };

    return (
        <div className="surface rounded-2xl p-6 space-y-4" data-testid="simulate-panel">
            <div className="flex items-center gap-2">
                <Play className="h-4 w-4 text-emerald-300" />
                <div className="label-eyebrow text-emerald-300">Dev / demo tool — labeled simulation</div>
            </div>
            <p className="text-sm text-slate-400">
                Type as a customer would on WhatsApp. The full AI pipeline runs live — classification,
                language detection, autonomy routing and reply generation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs mono uppercase tracking-widest text-slate-500">Customer phone</label>
                    <Input data-testid="sim-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                        className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
                </div>
                <div>
                    <label className="text-xs mono uppercase tracking-widest text-slate-500">Customer name</label>
                    <Input data-testid="sim-name" value={name} onChange={(e) => setName(e.target.value)}
                        className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
                </div>
            </div>
            <div>
                <label className="text-xs mono uppercase tracking-widest text-slate-500">Message</label>
                <Textarea data-testid="sim-text" value={text} onChange={(e) => setText(e.target.value)} rows={2}
                    className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
            </div>
            <div>
                <Button onClick={run} disabled={busy || !text.trim()} data-testid="sim-send"
                    className="bg-emerald-500 hover:bg-emerald-400 text-white gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Send simulated message
                </Button>
            </div>
            {result && (
                <pre data-testid="sim-result" className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-3 text-xs text-slate-300 overflow-x-auto">
                    {JSON.stringify(result, null, 2)}
                </pre>
            )}
            <p className="text-[11px] text-slate-500">
                Tip: try "I want to talk to a person" to see handover; "मुझे कल appointment चाहिए" for Hindi;
                or a healthcare-sensitive message like "severe tooth pain, what should I take?" on Bright Smile.
            </p>
        </div>
    );
}

function RulesPanel({ businessId }) {
    const [rules, setRules] = useState([]);
    const [nl, setNl] = useState("If someone asks for our address, reply automatically");
    const [parsed, setParsed] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!businessId) return;
        try {
            const r = await api.waListRules(businessId);
            setRules(r.rules || []);
        } catch {}
    };
    useEffect(() => { load(); }, [businessId]); // eslint-disable-line

    const parse = async () => {
        setBusy(true); setParsed(null);
        try {
            const r = await api.waParseRule(businessId, nl);
            setParsed(r.parsed);
        } catch { toast.error("Parse failed."); }
        finally { setBusy(false); }
    };
    const save = async () => {
        if (!parsed) return;
        setBusy(true);
        try {
            await api.waSaveRule(businessId, parsed);
            toast.success("Rule saved.");
            setParsed(null); setNl("");
            load();
        } catch { toast.error("Save failed."); }
        finally { setBusy(false); }
    };
    const toggle = async (id, enabled) => {
        try {
            await api.waToggleRule(id, enabled);
            load();
        } catch { toast.error("Toggle failed."); }
    };
    const del = async (id) => {
        try {
            await api.waDeleteRule(id);
            load();
        } catch { toast.error("Delete failed."); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rules-panel">
            <div className="surface rounded-2xl p-5 space-y-3">
                <div className="label-eyebrow text-cyan-300">Create automation (natural language)</div>
                <Textarea data-testid="rule-nl-input" value={nl} onChange={(e) => setNl(e.target.value)} rows={3}
                    className="bg-white/[0.03] border-white/10 text-slate-100"
                    placeholder='e.g. "If a message contains complaint or refund, notify manager"' />
                <div className="flex gap-2">
                    <Button onClick={parse} disabled={busy || !nl.trim()} data-testid="rule-parse-btn"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        Parse with AI
                    </Button>
                    {parsed && (
                        <Button onClick={save} disabled={busy} data-testid="rule-save-btn"
                            className="bg-emerald-500 hover:bg-emerald-400 text-white gap-1.5">
                            <Plus className="h-4 w-4" /> Save rule
                        </Button>
                    )}
                </div>
                {parsed && (
                    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-xs text-slate-300" data-testid="rule-parsed">
                        <div className="text-[10px] mono uppercase tracking-widest text-slate-500 mb-1">Parsed rule</div>
                        <div><span className="text-slate-500">Title:</span> {parsed.title}</div>
                        <div><span className="text-slate-500">Trigger:</span> {JSON.stringify(parsed.trigger)}</div>
                        <div><span className="text-slate-500">Action:</span> {JSON.stringify(parsed.action)}</div>
                        <div><span className="text-slate-500">Confidence:</span> {(parsed.confidence * 100).toFixed(0)}%</div>
                    </div>
                )}
            </div>
            <div className="surface rounded-2xl p-5 space-y-2">
                <div className="label-eyebrow text-slate-300">Active rules</div>
                {rules.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center">No rules yet.</div>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {rules.map((r) => (
                            <li key={r.id} data-testid={`rule-item-${r.id}`}
                                className="py-3 flex items-start gap-3">
                                <Switch checked={!!r.enabled} onCheckedChange={(v) => toggle(r.id, v)}
                                    data-testid={`rule-toggle-${r.id}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-slate-100">{r.title}</div>
                                    <div className="text-xs text-slate-400 truncate">{r.natural_language}</div>
                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                        <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400">
                                            trg: {r.trigger?.kind}={String(r.trigger?.value)}
                                        </Badge>
                                        <Badge variant="outline" className="text-[10px] border-indigo-500/30 bg-indigo-500/10 text-indigo-200">
                                            act: {r.action?.kind}
                                        </Badge>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => del(r.id)}
                                    data-testid={`rule-delete-${r.id}`}
                                    className="border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 gap-1 h-8">
                                    <Ban className="h-3 w-3" />
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function AutonomyPanel({ businessId }) {
    const [aut, setAut] = useState(null);
    const [saving, setSaving] = useState(null);
    const load = async () => {
        if (!businessId) return;
        try {
            const r = await api.waGetAutonomy(businessId);
            setAut(r.autonomy);
        } catch {}
    };
    useEffect(() => { load(); }, [businessId]); // eslint-disable-line

    const change = async (intent, mode) => {
        setSaving(intent);
        try {
            const r = await api.waUpdateAutonomy(businessId, { [intent]: mode });
            setAut(r.autonomy);
            toast.success(`${intent} → ${mode}`);
        } catch { toast.error("Save failed."); }
        finally { setSaving(null); }
    };

    if (!aut) return <div className="text-center py-16 text-slate-500"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>;
    const modes = ["SUGGEST", "APPROVAL", "AUTOPILOT", "ESCALATE"];
    return (
        <div className="surface rounded-2xl p-6 space-y-3" data-testid="autonomy-panel">
            <p className="text-sm text-slate-400">
                Choose how the AI responds to each message type. Complaints and human-handover always land in the Approval Inbox regardless of settings for safety.
            </p>
            {aut.intent_types?.map((k) => (
                <div key={k} className="flex items-center justify-between gap-4 py-2 border-b border-white/5">
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-100">{INTENT_LABELS[k] || k}</div>
                        <div className="text-xs text-slate-500">{aut.mode_labels?.[aut.rules[k]] || ""}</div>
                    </div>
                    <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-xl p-1">
                        {modes.map((m) => (
                            <button key={m} onClick={() => change(k, m)}
                                data-testid={`autonomy-${k}-${m.toLowerCase()}`}
                                disabled={saving === k}
                                className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                                    aut.rules[k] === m ? "bg-indigo-500 text-white"
                                                       : "text-slate-400 hover:text-slate-100"
                                }`}>
                                {m}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
